<template>
  <div class="table-list-root">
    <div v-if="modern" class="modern-table-wrapper">
      <div class="modern-table-header modern-table-grid" :style="modernGridStyle">
        <v-checkbox
          v-model="allSelected"
          :style="hideCheckbox && 'display:none'"
          hide-details
          dense
          class="modern-checkbox"
          color="#0277bd"
          @change="handleSelectAll"
          @click.native.stop
        />
        <button
          v-for="(item, i) in visibleFields"
          :key="`${item}/${i}`"
          type="button"
          class="modern-header-cell"
          @click="$emit('sort', item)"
        >
          <span>{{ workflowFieldLabel(item) }}</span>
          <v-icon v-if="pagination && pagination.sortBy === item" class="modern-sort-icon" size="16">
            {{ pagination.descending ? 'arrow_downward' : 'arrow_upward' }}
          </v-icon>
        </button>
        <div class="modern-action-spacer" />
      </div>
      <div
        v-for="attendee in displayedAttendees"
        :key="attendee.id"
        class="modern-table-row modern-table-grid"
        :class="{
          'modern-table-row-hover': isHovering(attendee.id),
          'modern-table-row-selected': selectedCopy[attendee.id],
          'table-row-skeleton': attendee.isLoadingSkeleton,
        }"
        :style="modernGridStyle"
        @click="$emit('click', getClickValue(attendee))"
        @mouseover="!isMobile && (hoveringRowId = attendee.id)"
        @mouseout="!isMobile && (hoveringRowId = undefined)"
      >
        <v-checkbox
          :style="hideCheckbox && 'display:none'"
          :value="selectedCopy[attendee.id]"
          hide-details
          dense
          class="modern-checkbox"
          color="#0277bd"
          @change="updateSelected(attendee.id, $event)"
          @click.native.stop
        />
        <div
          v-for="(eventField, index) in visibleFields"
          :key="index"
          class="modern-cell"
          :class="{ 'modern-cell-selected': selectedCopy[attendee.id] }"
          :data-label="workflowFieldLabel(eventField)"
          :title="workflowLabel(displayAttendeeField(attendee, eventField))"
        >
          {{ workflowLabel(displayAttendeeField(attendee, eventField)) }}
        </div>
        <div
          :class="[
            'modern-row-actions',
            { 'modern-row-actions-visible': isHovering(attendee.id) || isMobile },
          ]"
        >
          <button type="button" class="modern-row-action" @click.stop="() => print(attendee.uuid || attendee.id)">
            <v-icon color="#0277bd" size="22">print</v-icon>
          </button>
          <button
            type="button"
            class="modern-row-action"
            @click.stop="
              () => {
                deleteDialogAttendee = attendee.uuid
                isDialogOpen = true
              }
            "
          >
            <v-icon color="#db384c" size="22">delete</v-icon>
          </button>
        </div>
      </div>
      <delete-dialog
        :content="workflowLabel((adminText('workflowsAll.are_you_sure_you_want_to_delete_accreditation_bedaf86', 'Are you sure you want to delete accreditation? This operation cannot be undone.')))"
        :on-delete="() => deleteAttendee(deleteDialogAttendee, eventId)"
        :show.sync="isDialogOpen"
        :title="adminText('workflowsAll.delete_accreditation_2da0b1b', 'Delete accreditation')"
      />
      <v-snackbar v-model="showCopySnackbar" :timeout="2000" bottom right color="success">
        {{ adminText('workflowsAll.email_copied_47a1583', 'Email copied') }}
      </v-snackbar>
    </div>
    <v-layout v-else row class="wrapper overflow-wrapper border__bottom">
      <!-- FIXED CONTAINER -->
      <v-flex
        :style="{ 'min-width': `calc(100% / ${visibleFields.length})` }"
        class="border__left"
        shrink
      >
        <v-layout column>
          <table-header
            shrink
            :items="workflowUi([headerItems.fixed])"
            :pagination="pagination"
            :prepend-sale-status-column="$router.currentRoute.name === 'tickets'"
            @sort="$emit('sort', $event)"
          >
            <template v-slot:prefix>
              <v-checkbox
                v-model="allSelected"
                :style="hideCheckbox && 'display:none'"
                hide-details
                dense
                class="action_checkbox align-center justify-center"
                background-color="#f5f5f5"
                color="#666666"
                @change="handleSelectAll"
              />
            </template>
          </table-header>
          <!-- :style="{ 'grid-auto-columns': `50px minmax(150px, calc(100% / ${visibleFields.length}))` }" -->
          <div
            v-for="attendee in displayedAttendees"
            :key="attendee.id"
            class="table_grid_fixed"
            :style="`
            ${bodyStyle};
            ${isHovering(attendee.id) && `background-color: ${findBackgroundHoverColor(attendee)}`}
          `"
            :class="{ table_grid_skeleton: attendee.isLoadingSkeleton }"
            @click="$emit('click', getClickValue(attendee))"
            @mouseover="!isMobile && (hoveringRowId = attendee.id)"
            @mouseout="!isMobile && (hoveringRowId = undefined)"
          >
            <div @click.stop>
              <color-toggle-button
                v-if="showToggleButton"
                :label="workflowLabel(findToggleLabel(attendee))"
                :type="findToggleType(attendee)"
                :value="isOrderItemActive(attendee)"
                class="toggle-container"
                @changeToggleValue="value => updateToggleSwitch(attendee.id, value)"
              />
              <v-checkbox
                v-else
                :style="hideCheckbox && 'display:none'"
                :value="selectedCopy[attendee.id]"
                hide-details
                dense
                :disabled="attendee.hasOwnProperty('isSoldOut') ? !attendee.isSoldOut : false"
                class="align-center justify-center action_checkbox_fixed"
                @change="updateSelected(attendee.id, $event)"
              />
            </div>
            <div class="list_element">
              <span v-if="headerItems.fixed === 'Status' && attendee.status">
                {{ workflowLabel(attendee.status) }}
              </span>
              <v-layout v-if="headerItems.fixed === 'Category'" row>
                <v-icon :color="attendee.category.color" size="13" style="padding-right: 6px">
                  fiber_manual_record
                </v-icon>
                <span>{{ workflowLabel(attendee.category.name) }}</span>
              </v-layout>
              <span v-else>
                <span v-if="headerItems.fixed === 'Broj mobitela'" row>
                  {{ workflowLabel(displayPhoneNumber(attendee.fields[headerItems.fixed])) }}
                </span>
                <span v-else-if="enableEmailCopy && headerItems.fixed === 'Email' && attendee.email">
                  <v-tooltip bottom :disabled="isMobile">
                    <span
                      slot="activator"
                      v-clipboard:copy="attendee.email"
                      v-clipboard:success="handleCopySuccess"
                      v-clipboard:error="handleCopyError"
                      class="copyable-email"
                      @click.stop
                    >
                      {{ workflowLabel(attendee.email) }}
                    </span>
                    <span>{{ adminText('workflowsAll.click_to_copy_8ef0779', 'Click to copy') }}</span>
                  </v-tooltip>
                </span>
                <span
                  v-else-if="
                    Object.keys(attendeeFields).length !== 0 &&
                      attendeeFields[headerItems.fixed] &&
                      attendeeFields[headerItems.fixed].type.toLowerCase() === 'country' &&
                      attendee.fields[headerItems.fixed]
                  "
                  row
                >
                  <img
                    style="margin-right: 6px"
                    width="20"
                    height="12"
                    :src="`${frontendURL}/assets/flags/${attendee.fields[
                      headerItems.fixed
                    ].toLowerCase()}.svg`"
                  >
                  {{ workflowTicketCell(attendee.fields, headerItems.fixed) }}
                </span>
                <span v-else>
                  <v-icon
                    v-if="
                      ['tickets', 'promo-codes'].includes($router.currentRoute.name) &&
                        ['discount', 'price'].includes(headerItems.fixed.toLowerCase())
                    "
                    color="red"
                    size="13"
                  >fiber_manual_record</v-icon>
                  {{ workflowLabel(displayAttendeeField(attendee, headerItems.fixed)) }}
                </span>
              </span>
            </div>
          </div>
        </v-layout>
      </v-flex>

      <!-- SCROLLABLE CONTAINER -->
      <v-flex ref="scrollableElement" class="wrapper_scrollable border__right" grow>
        <div>
          <table-header
            :items="workflowUi(headerItems.scrollable)"
            :pagination="pagination"
            @sort="$emit('sort', $event)"
          />
          <div
            v-for="attendee in displayedAttendees"
            :key="attendee.id"
            class="table_grid"
            :style="`
            grid-auto-columns: minmax(170px, calc(100% / ${headerItems.scrollable.length}));
          `"
            :class="{ table_grid_skeleton: attendee.isLoadingSkeleton }"
            @click="$emit('click', getClickValue(attendee))"
            @mouseover="!isMobile && (hoveringRowId = attendee.id)"
            @mouseout="!isMobile && (hoveringRowId = undefined)"
          >
            <div
              v-for="(eventField, index) in headerItems.scrollable"
              :key="index"
              class="list_element"
              :style="`${
                isHovering(attendee.id) && `background-color: ${findBackgroundHoverColor(attendee)}`
              }`"
            >
              <v-layout v-if="eventField === 'Category'" row>
                <v-icon :color="attendee.category.color" size="13" style="padding-right: 6px">
                  fiber_manual_record
                </v-icon>
                <span>{{ workflowLabel(attendee.category.name) }}</span>
              </v-layout>
              <v-layout v-else-if="eventField === 'Color'" row>
                <v-icon
                  :color="attendee.color.hexadecimal"
                  size="1.8rem"
                  style="padding-right: 0.5rem"
                >
                  fiber_manual_record
                </v-icon>
                <span style="margin-top: 2px">{{ workflowLabel(attendee.color.name) }}</span>
              </v-layout>
              <v-layout
                v-else-if="eventField === 'Ticket category' && attendee.organizationItemCategory"
                row
              >
                <v-icon
                  :color="attendee.organizationItemCategory.color"
                  size="1.8rem"
                  style="padding-right: 0.5rem"
                >
                  fiber_manual_record
                </v-icon>
                <span style="margin-top: 2px">{{ workflowLabel(attendee.organizationItemCategory.name) }}</span>
              </v-layout>
              <v-layout
                v-else-if="
                  eventField === '' &&
                    [
                      'sport-organization-subscriptions-list',
                      'sport-organization-members-list',
                    ].includes($router.currentRoute.name)
                "
                style="color: #0277bd"
                @click.stop="$emit('send-email', attendee.id)"
              >
                {{ adminText('workflowsAll.send_email_619a901', 'Send email') }}
              </v-layout>
              <span v-else>
                <span v-if="eventField === 'Created at'">{{
                  workflowLabel(attendee['Created at'])
                }}</span>
                <span v-if="eventField === 'Created At'">{{
                  workflowLabel(displayCreatedAt(attendee.createdAt))
                }}</span>
                <span v-else-if="eventField === 'Status' && attendee.status">
                  {{ workflowLabel(attendee.status) }}
                </span>
                <span v-else-if="eventField === 'Broj mobitela'">
                  {{ workflowLabel(displayPhoneNumber(attendee.fields[eventField])) }}
                </span>
                <span v-else-if="enableEmailCopy && eventField === 'Email' && attendee.email">
                  <v-tooltip bottom :disabled="isMobile">
                    <span
                      slot="activator"
                      v-clipboard:copy="attendee.email"
                      v-clipboard:success="handleCopySuccess"
                      v-clipboard:error="handleCopyError"
                      class="copyable-email"
                      @click.stop
                    >
                      {{ workflowLabel(attendee.email) }}
                    </span>
                    <span>{{ adminText('workflowsAll.click_to_copy_8ef0779', 'Click to copy') }}</span>
                  </v-tooltip>
                </span>
                <span
                  v-else-if="
                    Object.keys(attendeeFields).length !== 0 &&
                      eventField !== 'Created At' &&
                      attendeeFields[eventField] &&
                      attendeeFields[eventField].type.toLowerCase() === 'country' &&
                      attendee.fields[eventField]
                  "
                  row
                >
                  <img
                    style="margin-right: 6px"
                    width="20"
                    height="12"
                    :src="`${frontendURL}/assets/flags/${attendee.fields[
                      eventField
                    ].toLowerCase()}.svg`"
                  >
                  {{ workflowTicketCell(attendee.fields, eventField) }}
                </span>
                <span v-else>
                  <v-icon
                    v-if="
                      ['tickets', 'promo-codes'].includes($router.currentRoute.name) &&
                        ['discount'].includes(eventField.toLowerCase())
                    "
                    color="red"
                    size="13"
                    style="padding-right: 6px"
                  >fiber_manual_record</v-icon>
                  {{ workflowLabel(displayAttendeeField(attendee, eventField)) }}
                </span>
              </span>
            </div>
            <div
              v-if="['accreditations', 'curated-invitations'].includes($router.currentRoute.name)"
              :class="[
                'row_action_container',
                { row_action_container__hover: isHovering(attendee.id) },
              ]"
            >
              <div class="row_action_button" @click.stop="() => print(attendee.uuid || attendee.id)">
                <v-icon color="#0277bd">print</v-icon>
              </div>
              <div
                class="row_action_button"
                @click.stop="
                  () => {
                    deleteDialogAttendee = attendee.uuid
                    isDialogOpen = true
                  }
                "
              >
                <v-icon class="delete-icon">delete</v-icon>
              </div>
            </div>
            <div
              v-if="['tickets', 'tickets-sport', 'promo-codes'].includes($router.currentRoute.name)"
              :class="[
                'row_action_container',
                { row_action_container__hover: isHovering(attendee.id) },
                {
                  row_action_container_ticketing_mobile:
                    isMobile && ['tickets', 'tickets-sport'].includes($router.currentRoute.name),
                },
              ]"
            >
              <div
                class="row_action_button"
                @click.stop="
                  () => {
                    $emit('orderDelete', attendee.id)
                  }
                "
              >
                <v-icon class="delete-icon">delete</v-icon>
              </div>
            </div>
          </div>
        </div>
      </v-flex>
      <delete-dialog
        :content="workflowLabel((adminText('workflowsAll.are_you_sure_you_want_to_delete_accreditation_bedaf86', 'Are you sure you want to delete accreditation? This operation cannot be undone.')))"
        :on-delete="() => deleteAttendee(deleteDialogAttendee, eventId)"
        :show.sync="isDialogOpen"
        :title="adminText('workflowsAll.delete_accreditation_2da0b1b', 'Delete accreditation')"
      />
      <v-snackbar v-model="showCopySnackbar" :timeout="2000" bottom right color="success">
        {{ adminText('workflowsAll.email_copied_47a1583', 'Email copied') }}
      </v-snackbar>
    </v-layout>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import ColorToggleButton from '@/components/shared/inputs/color-toggle-button.vue'
import { ColorToggleTypes } from '@/types'
import moment from 'moment-timezone'
import { DELETE_ATTENDEE } from '@/graphql/accreditation.graphql'
import { frontendURL } from '@/utils'
import DeleteDialog from '../../shared/dialogs/delete-dialog'
import TableHeader from './header.vue'
import { sportOrganizationRouteNames } from '@/routing/routeNames/sportOrganizerRouteNames'
import { theaterOrganizerRouteNames } from '@/routing/routeNames/theaterOrganizerRouterNames'

export default {
  mixins: [WorkflowDisplay],
  name: 'table-list',
  components: {
    ColorToggleButton,
    TableHeader,
    DeleteDialog,
  },
  props: {
    attendees: {
      type: Array,
      default: () => [],
    },
    organizationSlug: String,
    eventId: String,
    visibleFields: {
      type: Array,
      default: () => [],
    },
    selected: Array,
    pagination: {
      type: Object,
      default: () => {},
    },
    attendeeFields: {
      type: Object,
      default: () => {},
    },
    bodyStyle: {
      type: String,
    },
    hideCheckbox: {
      type: Boolean,
      default: false,
    },
    showToggleButton: {
      type: Boolean,
      default: false,
    },
    clickField: {
      type: String,
      default: 'id',
    },
    modern: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingRows: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      hoveringRowId: undefined,
      allSelected: this.selected.length > 0 && this.selected.length === this.attendees.length,
      selectedCopy: this.mapSelectedToObject(this.selected),
      frontendURL: '',
      deleteDialogAttendee: null,
      isDialogOpen: false,
      isDesktop: false,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      showCopySnackbar: false,
    }
  },
  computed: {
    headerItems() {
      return {
        fixed: this.visibleFields[0],
        scrollable: this.visibleFields.slice(1),
      }
    },
    displayedAttendees() {
      if (!this.loading) {
        return this.attendees
      }

      return Array.from({ length: this.loadingRows }, (_, index) => ({
        id: `loading-skeleton-${index}`,
        isLoadingSkeleton: true,
      }))
    },
    modernGridStyle() {
      return {
        gridTemplateColumns: `24px ${this.visibleFields
          .map(field => this.modernColumnWidth(field))
          .join(' ')} 104px`,
      }
    },
  },
  watch: {
    selected(val) {
      this.selectedCopy = this.mapSelectedToObject(val)
      if (val.length === 0) this.allSelected = false
      else if (val.length === this.attendees.length) this.allSelected = true
    },
  },
  methods: {
    getClickValue(attendee) {
      return attendee[this.clickField] || attendee.id
    },
    handleCopySuccess() {
      this.showCopySnackbar = true
    },
    handleCopyError() {
      this.showCopySnackbar = true
    },
    displayPhoneNumber(phoneField) {
      if (!phoneField) {
        return ''
      }

      let phone
      try {
        phone = JSON.parse(phoneField)
      } catch (e) {
        // Not a valid JSON, so it's a plain string.
        return phoneField
      }

      if (phone && typeof phone === 'object') {
        return `${phone.numberPrefix || ''} ${phone.number || ''}`.trim()
      }

      return phoneField
    },
    displayCreatedAt(createdAt) {
      return moment(createdAt).format('MM/DD/YYYY HH:mm')
    },
    displayCroatianDateTime(dateTime) {
      const parsedDate = moment(dateTime)

      return parsedDate.isValid() ? parsedDate.tz('Europe/Zagreb').format('DD.MM.YYYY. HH:mm') : ''
    },
    displayAttendeeField: function (attendee, eventField) {
      if (eventField === 'Type' && attendee.adminTicketRow) return this.workflowTicketCell(attendee, 'Type')
      const modernValue = this.displayModernAccreditationField(attendee, eventField)
      if (modernValue !== null) {
        return modernValue
      }

      if (['entryfrom', 'entryto'].includes(eventField.toLowerCase())) {
        return this.displayCroatianDateTime(attendee.fields && attendee.fields[eventField])
      }

      if (
        [
          'organization-promo-code-info',
          'organization-promo-codes',
          'tickets',
          'promo-codes',
          'organization-subscribers',
          'theater-organization-genres',
          'theater-tickets',
          'tickets-sport',
          'theater-organization-mailing-list',
          'theater-organization-tags',
          'theater-organization-ticket-type',
          'theater-organization-subscription-list',
          'theater-organization-subscription-plan',
          'theater-organization-memberships',
          'theater-organization-members-list',
          'theater-organization-promo-codes',
          'theater-organization-promo-code-info',
          'theater-accreditations',
          'ticket-categories',
          'sport-organization-subscriptions',
          'sport-organization-subscriptions-list',
          'sport-organization-promo-codes',
          'sport-organization-promo-code-info',
          'sport-organization-ticket-categories',
          'sport-organization-subscribers',
          'sport-organization-memberships',
          sportOrganizationRouteNames.sportOrganizationMembersList,
        ].includes(this.$router.currentRoute.name)
      ) {
        if (eventField.toLowerCase() === 'price') {
          return `${attendee[eventField.toLowerCase()]} ${attendee['currency']}`
        } else if (eventField.toLowerCase() === 'start date') {
          return attendee['from']
        } else if (eventField.toLowerCase() === 'member id no.') {
          return attendee['code']
        } else if (eventField.toLowerCase() === 'end date') {
          return attendee['to']
        } else if (eventField.toLowerCase() === 'expires') {
          return attendee['expires']
        } else if (eventField.toLowerCase() === 'subscription plan') {
          return attendee['Subscription plan']
        } else if (eventField.toLowerCase() === 'subscription status') {
          return attendee['Subscription status']
        } else if (eventField.toLowerCase() === 'duration') {
          return `${moment(attendee['from']).format('DD.MM.YYYY')} - ${moment(
            attendee['to'],
          ).format('DD.MM.YYYY')}`
        } else if (eventField.toLowerCase() === 'ticket') {
          return attendee['orderItem']['name']
        } else if (
          eventField.toLowerCase() === 'ticket name' ||
          eventField.toLowerCase() === 'plan'
        ) {
          return attendee['name']
        } else if (eventField.toLowerCase() === 'promo code') {
          return attendee['code']
        } else if (eventField.toLowerCase() === 'sales channel') {
          return attendee.ticketSellersLabel
        } else if (eventField.toLowerCase() === 'attendee') {
          return attendee['name']
        } else if (eventField.toLowerCase() === 'email') {
          return attendee['email']
        } else if (eventField.toLowerCase() === 'tickets type (no.)') {
          return attendee['ticketTypes']
        } else if (eventField.toLowerCase() === 'no. of tickets') {
          return attendee['ticketNumber']
        } else if (eventField.toLowerCase() === 'ticket category name') {
          return attendee['name']
        } else if (eventField.toLowerCase() === 'w/ access code') {
          return attendee['accessCode']
        }

        return attendee[eventField.toLowerCase()]
      }

      const attendeeFieldValue = this.profileFieldValue(attendee.fields, eventField)

      if (!attendeeFieldValue) {
        return this.buyerProfileFieldValue(attendee, eventField)
      }

      return Array.isArray(attendeeFieldValue) ? attendeeFieldValue.join(', ') : attendeeFieldValue
    },
    displayModernAccreditationField(attendee, eventField) {
      if (!this.modern) return null

      const fields = attendee.fields || {}
      const orderBuyerFields = this.buyerProfileFields(attendee)
      const valueByField = {
        'Ticket Owner': this.formatFullName(fields) || fields['Attendee name'],
        'Document ID': this.firstValue(fields, ['Document ID', 'Document Id', 'OIB', 'oib', 'ID']),
        'Owner Email': this.firstValue(fields, ['Owner Email', 'Email', 'email']),
        Function: this.firstValue(fields, ['Function', 'function', 'Funkcija', 'funkcija']),
        'Ticket Buyer': this.formatFullName(orderBuyerFields) || orderBuyerFields.name,
        'Buyer Email': this.firstValue(orderBuyerFields, ['email', 'Email', 'Buyer Email']),
        Address:
          this.profileFieldValue(fields, 'Address') ||
          this.buyerProfileFieldValue(attendee, 'Address'),
        address:
          this.profileFieldValue(fields, 'address') ||
          this.buyerProfileFieldValue(attendee, 'address'),
        City: this.profileFieldValue(fields, 'City') || this.buyerProfileFieldValue(attendee, 'City'),
        city: this.profileFieldValue(fields, 'city') || this.buyerProfileFieldValue(attendee, 'city'),
        'Postal Code':
          this.profileFieldValue(fields, 'Postal Code') ||
          this.buyerProfileFieldValue(attendee, 'Postal Code'),
        'Postal code':
          this.profileFieldValue(fields, 'Postal code') ||
          this.buyerProfileFieldValue(attendee, 'Postal code'),
        postalCode:
          this.profileFieldValue(fields, 'postalCode') ||
          this.buyerProfileFieldValue(attendee, 'postalCode'),
        Country:
          this.profileFieldValue(fields, 'Country') ||
          this.buyerProfileFieldValue(attendee, 'Country'),
        country:
          this.profileFieldValue(fields, 'country') ||
          this.buyerProfileFieldValue(attendee, 'country'),
        'Created At': attendee.createdAt ? moment(attendee.createdAt).format('D.M.YYYY') : '',
      }

      if (!Object.prototype.hasOwnProperty.call(valueByField, eventField)) return null

      return valueByField[eventField] || ''
    },
    buyerProfileFields(attendee) {
      const buyer = (attendee.order && attendee.order.buyer) || {}

      return buyer.fields || buyer || {}
    },
    profileFieldKeys(eventField) {
      const fieldKeys = {
        Address: ['Address', 'address'],
        address: ['address', 'Address'],
        City: ['City', 'city'],
        city: ['city', 'City'],
        'Postal Code': ['Postal Code', 'Postal code', 'postalCode', 'zipCode'],
        'Postal code': ['Postal code', 'Postal Code', 'postalCode', 'zipCode'],
        postalCode: ['postalCode', 'Postal code', 'Postal Code', 'zipCode'],
        zipCode: ['zipCode', 'postalCode', 'Postal code', 'Postal Code'],
        Country: ['Country', 'country'],
        country: ['country', 'Country'],
      }

      return fieldKeys[eventField] || [eventField]
    },
    profileFieldValue(fields = {}, eventField) {
      return this.firstValue(fields || {}, this.profileFieldKeys(eventField))
    },
    buyerProfileFieldValue(attendee, eventField) {
      return this.profileFieldValue(this.buyerProfileFields(attendee), eventField)
    },
    firstValue(fields, keys) {
      const key = keys.find(k => fields[k])

      return key ? fields[key] : ''
    },
    formatFullName(fields) {
      const firstName = this.firstValue(fields, ['firstName', 'First name', 'First Name', 'Ime'])
      const lastName = this.firstValue(fields, ['lastName', 'Last name', 'Last Name', 'Prezime'])

      return [firstName, lastName].filter(Boolean).join(' ').trim()
    },
    modernColumnWidth(field) {
      const widths = {
        'Attendee name': '200px',
        Email: '240px',
        email: '240px',
        'Owner Email': '240px',
        'Ticket Buyer': '200px',
        'Buyer Email': '260px',
        'Document ID': '140px',
        Function: '160px',
        Category: '160px',
        Address: '220px',
        address: '220px',
        City: '150px',
        city: '150px',
        'Postal Code': '140px',
        'Postal code': '140px',
        postalCode: '140px',
        zipCode: '140px',
        code: '120px',
        Code: '120px',
        Country: '160px',
        country: '160px',
        'Created At': '120px',
      }

      return widths[field] || '180px'
    },
    isHovering(id) {
      return this.hoveringRowId === id
    },
    updateSelected(attendeeId, selected) {
      this.selectedCopy[attendeeId] = selected
      const selectedIds = this.mapSelectedIds()

      if (selectedIds.length === this.attendees.length) this.allSelected = true
      else if (this.allSelected) this.allSelected = false

      return this.$emit('update:selected', selectedIds)
    },
    updateToggleSwitch(attendeeId, value) {
      this.$emit('updateTicketActivity', attendeeId, value)
    },
    mapSelectedToObject(selectedIds) {
      return selectedIds.reduce((acc, s) => {
        acc[s] = true

        return acc
      }, {})
    },
    mapSelectedIds() {
      return Object.entries(this.selectedCopy)
        .map(([id, value]) => (value ? id : undefined))
        .filter(i => i)
    },
    handleSelectAll(allSelected) {
      if (!allSelected) return this.$emit('update:selected', [])
      const deletingTickets = this.attendees.some(a => a.hasOwnProperty('isSoldOut'))
      const tickets = this.attendees.filter(a => a.isSoldOut).map(a => a.id)
      const selectAll = this.attendees.map(a => a.id)

      return this.$emit('update:selected', deletingTickets ? tickets : selectAll)
    },
    async deleteAttendee(attendeeUUID, eventId) {
      await this.$apollo.mutate({
        mutation: DELETE_ATTENDEE,
        variables: { eventId, attendeeUUID },
      })
      this.$emit('attendeeDeleted')
    },
    async print(attendeeId) {
      const url = `${frontendURL}/organizations/${this.organizationSlug}/events/${this.eventId}/accreditation/${attendeeId}/edit/print`
      window.open(url, '_blank', 'noopener')
    },
    isTicketSoldOut(ticket) {
      return ticket.quantity <= 0
    },
    isOrderItemActive(attendee) {
      if (attendee.event && attendee.event.soldOut) return false
      const currentDate = moment()

      return currentDate.isBetween(moment(attendee.fullDateFrom), moment(attendee.fullDateTo))
    },
    isTicketOnHold(ticket) {
      const currentDate = moment()

      return currentDate.isBefore(moment(ticket.fullDateFrom))
    },
    isTicketAvailable(ticket) {
      const currentDate = moment()

      return currentDate.isBetween(moment(ticket.fullDateFrom), moment(ticket.fullDateTo))
    },
    findToggleLabel(ticket) {
      if (ticket.event && ticket.event.soldOut) {
        return this.adminText('workflowsAll.closed_88d86b7', 'Closed')
      } else if (this.isTicketSoldOut(ticket)) {
        return this.adminText('workflowsAll.sold_out_76fd560', 'Sold Out')
      } else if (this.isTicketOnHold(ticket)) {
        return this.adminText('workflowsAll.on_hold_73fd29e', 'On Hold')
      } else if (!this.isTicketAvailable(ticket)) {
        return this.adminText('workflowsAll.closed_88d86b7', 'Closed')
      } else if (this.isTicketAvailable(ticket)) {
        return this.adminText('workflowsAll.available_7c62a14', 'Available')
      }
    },
    findToggleType(ticket) {
      if (ticket.event && ticket.event.soldOut) {
        return ColorToggleTypes.FAILED
      } else if (this.isTicketSoldOut(ticket)) {
        return ColorToggleTypes.DISABLED
      } else if (this.isTicketOnHold(ticket)) {
        return ColorToggleTypes.REGULAR
      } else if (this.isTicketAvailable(ticket)) {
        return ColorToggleTypes.SUCCESSFUL
      } else {
        return ColorToggleTypes.FAILED
      }
    },
    findBackgroundHoverColor(ticket) {
      if (this.$router.currentRoute.name !== 'tickets') {
        return '#F0F6FA'
      }

      if (this.isTicketAvailable(ticket)) {
        return '#EEF6EF'
      } else {
        return '#F0F6FA'
      }
    },
    enableEmailCopy() {
      return this.$router.currentRoute.name === theaterOrganizerRouteNames.accreditationsTheater
    },
  },
}
</script>

<style scoped>
.table-list-root {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.modern-table-wrapper {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  contain: inline-size;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #f0f6fa;
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  -webkit-overflow-scrolling: touch;
}

.modern-table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.modern-table-wrapper::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: rgba(102, 102, 102, 0.25);
}

.modern-table-wrapper::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.modern-table-grid {
  display: grid;
  column-gap: 16px;
  align-items: center;
  width: max-content;
  min-width: 100%;
  padding-left: 12px;
  padding-right: 16px;
  box-sizing: border-box;
}

.modern-table-header {
  height: 48px;
  background: #f5f5f5;
}

.modern-header-cell {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: #666666;
  cursor: pointer;
  font-family: Raleway, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  text-align: left;
}

.modern-header-cell span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modern-sort-icon {
  margin-left: 4px;
  color: #0277bd;
}

.modern-table-row {
  position: relative;
  height: 56px;
  background: #ffffff;
  border: 1px solid #f0f6fa;
  border-top: none;
  cursor: pointer;
}

.modern-table-row-hover {
  background: #f0f6fa;
}

.modern-table-row-selected {
  background: #e6f1f8;
}

.modern-cell {
  min-width: 0;
  overflow: hidden;
  color: #666666;
  font-size: 16px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modern-cell-selected {
  color: #0277bd;
}

.modern-checkbox {
  margin: 0;
  padding: 0;
}

.modern-action-spacer {
  width: 112px;
}

.modern-row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  opacity: 0;
  pointer-events: none;
}

.modern-row-actions-visible {
  opacity: 1;
  pointer-events: auto;
}

.modern-row-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 12px;
  border: none;
  border-radius: 24px;
  background: #ffffff;
  cursor: pointer;
}

.modern-row-action:hover {
  background: #f5f5f5;
}

@media (max-width: 800px) {
  .modern-table-wrapper {
    overflow-x: auto;
    border-left: 1px solid #f0f6fa;
    border-right: 1px solid #f0f6fa;
  }

  .modern-header-cell,
  .modern-cell {
    font-size: 15px;
  }

  .modern-row-action {
    width: 40px;
    height: 40px;
    padding: 8px;
  }
}

>>> .modern-checkbox .v-input__slot {
  margin: 0;
}

>>> .modern-checkbox .v-messages {
  display: none;
}

@media (min-width: 800px) {
  .wrapper_scrollable {
    overflow: auto;
  }
}

@media (max-width: 800px) {
  .overflow-wrapper {
    overflow: auto;
  }
}

.wrapper {
  font-size: 16px;
}

.wrapper_scrollable {
  width: 0;
}

.wrapper_scrollable::-webkit-scrollbar {
  -webkit-appearance: none;
}

.wrapper_scrollable::-webkit-scrollbar:horizontal {
  height: 11px;
}

.wrapper_scrollable::-webkit-scrollbar-thumb {
  border-radius: 8px;
  border: 2px solid white;
  background-color: rgba(0, 0, 0, 0.2);
}

.table_grid {
  cursor: pointer;
  display: grid;
  grid-auto-flow: column;
  height: 55px;
  /* float: left; */
}

.table_grid_fixed {
  cursor: pointer;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 50px minmax(200px, 150px);
  height: 55px;
}

.table_grid_skeleton,
.table-row-skeleton {
  pointer-events: none;
}

.table_grid_skeleton .list_element,
.table-row-skeleton .modern-cell {
  position: relative;
  color: transparent;
}

.table_grid_skeleton .list_element::after,
.table-row-skeleton .modern-cell::after {
  position: absolute;
  top: 50%;
  left: 12px;
  width: 70%;
  height: 16px;
  content: '';
  transform: translateY(-50%);
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f2f4 25%, #e4e7ea 50%, #f0f2f4 75%);
  background-size: 200% 100%;
  animation: table-row-skeleton-shimmer 1.4s ease-in-out infinite;
}

.table_grid_skeleton .list_element:nth-child(even)::after,
.table-row-skeleton .modern-cell:nth-child(even)::after {
  width: 54%;
}

.table_grid_skeleton .v-input,
.table_grid_skeleton .row_action_container,
.table-row-skeleton .modern-checkbox,
.table-row-skeleton .modern-row-actions {
  visibility: hidden;
}

@keyframes table-row-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.list_element {
  padding: 14px 12px;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* .list_element::-webkit-scrollbar {
    -webkit-appearance: none;
} */
::-webkit-scrollbar {
  height: 0px;
}

/* .list_element::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: rgba(0, 0, 0, .2);
} */

.list_element__text {
  font-family: 'Source Sans Pro';
  font-size: 16px;
  color: #333333;
}

.border__left {
  border-left: 2px solid #f5f5f5;
}

.border__right {
  border-right: 2px solid #f5f5f5;
}

.border__bottom {
  border-bottom: 2px solid #f5f5f5;
}

.action_checkbox {
  margin: 0;
  height: 40px;
  width: 0px;
  padding-left: 12px;
  text-align: center;
}

.action_checkbox_fixed {
  margin: 0;
  height: 50px;
  padding-left: 12px;
  text-align: center;
}

.row_action_container {
  display: flex;
  width: fit-content;
  flex-direction: row;
  padding: 12px 16px 12px 0;
  opacity: 0;
  position: absolute;
  right: 20px;
}

/*.row_action_container:hover {
  opacity: 1;
}*/

.row_action_button {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
}

.row_action_button:hover {
  background: rgba(2, 119, 189, 0.2);
}

.delete-icon {
  color: #db384c;
  background-color: #fbebed;
}

.delete-icon:hover {
  color: #bd2a3c;
}

.row_action_container__hover {
  opacity: 1;
}

.row_action_container_ticketing_mobile {
  opacity: 1;
  position: static;
  padding: 12px 0 12px 0;
  transform: translateX(-25px);
}

.copyable-email {
  cursor: pointer;
}

/*
  FADE EFFECT, commented out for now because it caused too many issues

 .fade_container {
  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;
  position: relative;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
/*
.fade_left::after {
  animation: fadeIn 0.35s;
  content: "";
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  bottom: 10px;
  height: 40px;
  pointer-events: none;
  background-image: linear-gradient(to left, rgba(255, 255, 255, 0), #f5f5f5 100%);
  width: 3%;
}


.fade_left::before {
  animation: fadeIn 0.35s;
  content: "";
  position: absolute;
  z-index: 1;
  top: 42px;
  left: 0;
  bottom: 15px;
  pointer-events: none;
  background-image: linear-gradient(to left, rgba(255, 255, 255, 0), white 100%);
  width: 3%; */
</style>
